import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type AuditType = 'eeat' | 'ai-audit' | 'landing-page';

export interface CitedDomainsUploadResponse {
  success: boolean;
  rows_processed: number;
  override: boolean;
  strict: boolean;
}

export interface AuditQuestionsInfo {
  audit_type: string;
  file_exists: boolean;
  file_type: 'custom' | 'default' | null;
  row_count: number;
  categories: string[];
  category_count: number;
}

export interface UploadResponse {
  status: string;
  message: string;
  audit_type: string;
  row_count: number;
  categories: string[];
  category_count: number;
}

export interface DiscoveryPage {
  id: number;
  domain: string;
  pages: string[];
  pages_text: { [key: string]: string };
  last_fetched_at: string | null;
  updated_at: string | null;
}

export interface DiscoveryPageListResponse {
  pages: DiscoveryPage[];
  total: number;
  page: number;
  per_page: number;
}

export interface DiscoveryCitation {
  id: number;
  brand: string;
  category: string;
  region: string;
  base_domain: string | null;
  last_fetched_at: string | null;
  updated_at: string | null;
  citation_count: number;
  /** Cited content (citation_data) when returned by list API */
  citation_data?: any;
  /** Competitors nested under this primary-brand cache row */
  competitors_in_run?: string[] | null;
}

export interface DiscoveryCitationListResponse {
  citations: DiscoveryCitation[];
  total: number;
  page: number;
  per_page: number;
}

export interface ServiceVerificationResponse {
  service: string;
  status: 'working' | 'failed';
  message: string;
  timestamp: string;
  details: { [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Upload CSV file for audit questions
   */
  uploadAuditQuestions(auditType: AuditType, file: File): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams().set('audit_type', auditType);

    return this.http.post<UploadResponse>(
      `${this.API_URL}/admin/audit-questions/upload`,
      formData,
      { params }
    );
  }

  /**
   * Download current audit questions CSV
   */
  downloadAuditQuestions(auditType: AuditType): Observable<Blob> {
    const params = new HttpParams().set('audit_type', auditType);

    return this.http.get(
      `${this.API_URL}/admin/audit-questions/download`,
      {
        params,
        responseType: 'blob'
      }
    );
  }

  /**
   * Get information about current audit questions
   */
  getAuditQuestionsInfo(auditType: AuditType): Observable<AuditQuestionsInfo> {
    const params = new HttpParams().set('audit_type', auditType);

    return this.http.get<AuditQuestionsInfo>(
      `${this.API_URL}/admin/audit-questions/info`,
      { params }
    );
  }

  /**
   * Upload cited domains CSV (settings)
   */
  uploadCitedDomains(file: File, override: boolean, strict: boolean): Observable<CitedDomainsUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams()
      .set('override', override)
      .set('strict', strict);

    return this.http.post<CitedDomainsUploadResponse>(
      `${this.API_URL}/settings/cited-domains/upload`,
      formData,
      { params }
    );
  }

  /**
   * Download cited domains CSV (settings)
   */
  downloadCitedDomains(): Observable<Blob> {
    return this.http.get(
      `${this.API_URL}/settings/cited-domains/download`,
      { responseType: 'blob' }
    );
  }

  /**
   * Get all discovery pages with pagination
   */
  getDiscoveryPages(skip: number = 0, limit: number = 100): Observable<DiscoveryPageListResponse> {
    const params = new HttpParams()
      .set('skip', skip.toString())
      .set('limit', limit.toString());

    return this.http.get<DiscoveryPageListResponse>(
      `${this.API_URL}/admin/discovery-pages/`,
      { params }
    );
  }

  /**
   * Delete a discovery page by ID
   */
  deleteDiscoveryPage(pageId: number): Observable<{ message: string; id: number }> {
    return this.http.delete<{ message: string; id: number }>(
      `${this.API_URL}/admin/discovery-pages/${pageId}`
    );
  }

  /**
   * Get all discovery citations with pagination
   */
  getDiscoveryCitations(skip: number = 0, limit: number = 100, search?: string): Observable<DiscoveryCitationListResponse> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('limit', limit.toString());
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<DiscoveryCitationListResponse>(
      `${this.API_URL}/admin/discovery-citations/`,
      { params }
    );
  }

  /**
   * Get discovery citation detail by ID
   */
  getDiscoveryCitationDetail(citationId: number): Observable<{
    id: number;
    brand: string;
    category: string;
    region: string;
    base_domain: string | null;
    citation_data: any;
    discovery_sections?: Record<string, unknown> | null;
    last_fetched_at: string | null;
    updated_at: string | null;
    competitor_job_active?: boolean;
  }> {
    return this.http.get<{
      id: number;
      brand: string;
      category: string;
      region: string;
      base_domain: string | null;
      citation_data: any;
      discovery_sections?: Record<string, unknown> | null;
      last_fetched_at: string | null;
      updated_at: string | null;
      competitor_job_active?: boolean;
    }>(`${this.API_URL}/admin/discovery-citations/${citationId}`);
  }

  startCompetitorDiscovery(citationId: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.API_URL}/admin/discovery-citations/${citationId}/competitor-discovery/start`,
      {}
    );
  }

  cancelCompetitorDiscovery(citationId: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.API_URL}/admin/discovery-citations/${citationId}/competitor-discovery/cancel`,
      {}
    );
  }

  /**
   * Delete a discovery citation by ID
   */
  deleteDiscoveryCitation(citationId: number): Observable<{ message: string; id: number }> {
    return this.http.delete<{ message: string; id: number }>(
      `${this.API_URL}/admin/discovery-citations/${citationId}`
    );
  }

  /**
   * Verify scraper service
   */
  verifyScraperService(): Observable<ServiceVerificationResponse> {
    return this.http.get<ServiceVerificationResponse>(
      `${this.API_URL}/admin/verify/scraper`
    );
  }

  /**
   * Verify Tavily service
   */
  verifyTavilyService(): Observable<ServiceVerificationResponse> {
    return this.http.get<ServiceVerificationResponse>(
      `${this.API_URL}/admin/verify/tavily`
    );
  }

  /**
   * Verify OpenAI service
   */
  verifyOpenAIService(): Observable<ServiceVerificationResponse> {
    return this.http.get<ServiceVerificationResponse>(
      `${this.API_URL}/admin/verify/openai`
    );
  }

  /**
   * Verify Gemini service
   */
  verifyGeminiService(): Observable<ServiceVerificationResponse> {
    return this.http.get<ServiceVerificationResponse>(
      `${this.API_URL}/admin/verify/gemini`
    );
  }

  /**
   * Verify web search credentials
   */
  verifyOxylabsService(): Observable<ServiceVerificationResponse> {
    return this.http.get<ServiceVerificationResponse>(
      `${this.API_URL}/admin/verify/oxylabs`
    );
  }

  /**
   * Get all API keys
   */
  getApiKeys(): Observable<ApiKeyResponse[]> {
    return this.http.get<ApiKeyResponse[]>(
      `${this.API_URL}/admin/api-keys/`
    );
  }

  /**
   * Get specific API key
   */
  getApiKey(serviceName: string): Observable<ApiKeyResponse> {
    return this.http.get<ApiKeyResponse>(
      `${this.API_URL}/admin/api-keys/${serviceName}`
    );
  }

  /**
   * Update API key
   */
  updateApiKey(serviceName: string, apiKey: string): Observable<ApiKeyUpdateResponse> {
    return this.http.put<ApiKeyUpdateResponse>(
      `${this.API_URL}/admin/api-keys/${serviceName}`,
      { api_key: apiKey }
    );
  }
}

export interface ApiKeyResponse {
  service_name: string;
  is_active: boolean;
  management_url: string | null;
  last_verified_at: string | null;
  last_verification_status: 'working' | 'failed' | null;
  updated_at: string;
  updated_by: string | null;
  api_key_masked: string;
}

export interface ApiKeyUpdateResponse {
  service_name: string;
  message: string;
  api_key_masked: string;
}






